import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import {
  AssignDialogStateType,
  ReplaceStaffDialogStateType,
  WaveSelectionType,
  TaskWaveType,
  WarehouseOptionType,
  NextProcessType,
} from 'features/SupplyOrder/pages/XdockV2/@types'
import {
  getLatestSupplyOrderPooling,
  getSupplyOrderPoolDetailListApi,
  getXdockTaskDetailApi,
  getXdockTaskListApi,
  getXdockTaskStaffListApi,
  postAssignXdockTaskApi,
  postCompletePoolingApi,
  type GetXdockTaskListParamsType,
  type GetSupplyOrderPoolDetailListParamsType,
} from 'features/SupplyOrder/services/xdockV2'
import { getLocationListByType } from 'features/Location/services/location'
import {
  SupplyOrderPoolingDetailInterface,
  XdockTaskStaffInterface,
} from 'features/SupplyOrder/@types/XDock'

export const SLICE_NAME = 'xdockV2'

export const fetchXdockTaskList = createAsyncThunk(
  `${SLICE_NAME}/fetchXdockTaskList`,
  async (params: GetXdockTaskListParamsType, { rejectWithValue }) => {
    try {
      const { data } = await getXdockTaskListApi(params)
      return {
        data: data.data,
        total: Number(data.pagination?.number_of_elements ?? data.data.length ?? 0),
      }
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

// Fetch Direct to Checker list — hits supply-order-pool-details with is_dtc always true
export const fetchDtcPoolDetailList = createAsyncThunk(
  `${SLICE_NAME}/fetchDtcPoolDetailList`,
  async (params: Omit<GetSupplyOrderPoolDetailListParamsType, 'is_dtc'>, { rejectWithValue }) => {
    try {
      const { data } = await getSupplyOrderPoolDetailListApi({ ...params, is_dtc: true })
      return {
        data: data.data,
        total: data.data.length,
      }
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

const STAFF_LIST_PAGE_SIZE = 20

const fetchStaffList = async (locationId: number | null, name?: string) => {
  const { data } = await getXdockTaskStaffListApi({
    ...(locationId ? { location_id: locationId } : {}),
    ...(name ? { name } : {}),
    page_index: 1,
    page_size: STAFF_LIST_PAGE_SIZE,
  })
  return data.data
}

// Fetch staff list for a given wave step, excluding already-selected staff IDs
export const fetchCrewListByWave = createAsyncThunk<
  XdockTaskStaffInterface[],
  { excludeIds?: number[]; name?: string }
>(`${SLICE_NAME}/fetchCrewListByWave`, async (params, { getState, rejectWithValue }) => {
  try {
    const { locationId } = (getState() as { xdockV2: { assignDialog: AssignDialogStateType } })
      .xdockV2.assignDialog
    const staffList = await fetchStaffList(locationId, params.name)
    return staffList.filter(({ staff }) => !(params.excludeIds ?? []).includes(staff.id))
  } catch (err) {
    callErrorMsg(err)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

// Fetch staff list for the Replace Staff dialog (single wave reassignment)
export const fetchReplaceStaffList = createAsyncThunk<XdockTaskStaffInterface[], { name?: string }>(
  `${SLICE_NAME}/fetchReplaceStaffList`,
  async (params, { getState, rejectWithValue }) => {
    try {
      const { locationId } = (
        getState() as { xdockV2: { replaceStaffDialog: ReplaceStaffDialogStateType } }
      ).xdockV2.replaceStaffDialog
      return await fetchStaffList(locationId, params.name)
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

// Submit a single-wave reassignment — backend upserts, other waves stay untouched
export const submitReplaceStaff = createAsyncThunk<
  boolean,
  { itemId: number; waveNumber: number; staffId: number }
>(`${SLICE_NAME}/submitReplaceStaff`, async (params, { rejectWithValue }) => {
  try {
    await postAssignXdockTaskApi(params.itemId, {
      waves: [{ wave_number: params.waveNumber, staff_id: params.staffId }],
    })
    toastSuccess('Berhasil ganti staf')
    return true
  } catch (err) {
    callErrorMsg(err)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

// Fetch wave detail for a sorting item (Task Detail dialog)
export const fetchTaskDetail = createAsyncThunk<
  {
    productName: string
    productImage: string
    sku_number: string
    waves: TaskWaveType[]
  },
  { itemId: number }
>(`${SLICE_NAME}/fetchTaskDetail`, async (params, { rejectWithValue }) => {
  try {
    const { data } = await getXdockTaskDetailApi(params.itemId)
    const { product, waves } = data.data
    return {
      productName: product?.name ?? '',
      productImage: product?.image?.url_small ?? '',
      sku_number: product?.sku_number ?? '',
      waves: waves ?? [],
    }
  } catch (err) {
    callErrorMsg(err)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

// Submit wave assignment — slice handles dialog close via extraReducers
// Waves with a wave_number send { wave_number, staff_id }; waves without send { staff_id } only
export const submitWaveAssignment = createAsyncThunk<
  boolean,
  { itemId: number; waveSelections: WaveSelectionType }
>(`${SLICE_NAME}/submitWaveAssignment`, async (params, { getState, rejectWithValue }) => {
  try {
    const { waves: dialogWaves } = (
      getState() as { xdockV2: { assignDialog: AssignDialogStateType } }
    ).xdockV2.assignDialog
    const waves = params.waveSelections
      .map((staffId, index) => {
        const waveNumber = dialogWaves[index]?.wave_number
        return {
          ...(typeof waveNumber === 'number' ? { wave_number: waveNumber } : {}),
          staff_id: staffId,
        }
      })
      .filter((wave): wave is { wave_number?: number; staff_id: number } => wave.staff_id !== null)
    await postAssignXdockTaskApi(params.itemId, { waves })
    toastSuccess('Berhasil assign tugas')
    return true
  } catch (err) {
    callErrorMsg(err)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})

// Fetch warehouse options for Complete Pooling wizard step 0
export const fetchWarehouseOptions = createAsyncThunk<WarehouseOptionType[], void>(
  `${SLICE_NAME}/fetchWarehouseOptions`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getLocationListByType('WAREHOUSE')
      return data.map((loc) => ({
        id: loc.location_id,
        code: loc.location_type,
        name: loc.location_name,
      }))
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

// Fetch pooling detail after warehouse selected in Complete Pooling wizard step 0
export const fetchPoolingDetail = createAsyncThunk<
  SupplyOrderPoolingDetailInterface,
  { location_id: number }
>(
  `${SLICE_NAME}/fetchPoolingDetail`,
  async (params: { location_id: number }, { rejectWithValue }) => {
    try {
      const { data } = await getLatestSupplyOrderPooling(params)
      return data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

// Submit complete pooling — slice resets dialog via extraReducers
export const submitCompletePooling = createAsyncThunk<
  boolean,
  { poolingId: number; nextProcess: NextProcessType }
>(`${SLICE_NAME}/submitCompletePooling`, async (params, { rejectWithValue }) => {
  try {
    await postCompletePoolingApi(params.poolingId, { type: params.nextProcess })
    toastSuccess('Berhasil menyelesaikan pooling')
    return true
  } catch (err) {
    callErrorMsg(err)
    return rejectWithValue(SOMETHING_WHEN_WRONG)
  }
})
